/**
 * Driver decomposition helpers. The backend (or the fixture engine) supplies
 * per-input contributions for one series at one week; this module checks that
 * they reconcile with the scenario difference, exposes the residual, and lays
 * the rows out for a horizontal waterfall. It never redistributes a residual
 * into a driver.
 */

/**
 * Reconcile a drivers block. Returns
 * `{ total, sum, residual, reconciled, tolerance, contributions }` where
 * `residual = total − sum` (computed here; the backend's own residual, when
 * present, is compared against it) and `reconciled` is |residual| ≤ tolerance.
 */
export function reconcile(block) {
  if (!block || !Array.isArray(block.contributions)) return null;
  const baseline = Number(block.baselineValue);
  const alternative = Number(block.alternativeValue);
  if (!Number.isFinite(baseline) || !Number.isFinite(alternative)) return null;
  const contributions = block.contributions
    .map((c) => ({ input: c.input, contribution: Number(c.contribution), unit: c.unit || "" }))
    .filter((c) => Number.isFinite(c.contribution));
  const total = alternative - baseline;
  const sum = contributions.reduce((s, c) => s + c.contribution, 0);
  const residual = total - sum;
  const tolerance = Number.isFinite(Number(block.tolerance)) ? Number(block.tolerance) : Math.max(1e-9, Math.abs(total) * 1e-6);
  const backendResidual = Number.isFinite(Number(block.residual)) ? Number(block.residual) : null;
  return {
    baseline,
    alternative,
    total,
    sum,
    residual,
    backendResidual,
    tolerance,
    reconciled: Math.abs(residual) <= tolerance,
    backendAgrees: backendResidual == null ? null : Math.abs(backendResidual - residual) <= tolerance,
    contributions: contributions.slice().sort((a, b) => Math.abs(b.contribution) - Math.abs(a.contribution)),
  };
}

/**
 * Waterfall rows: baseline → each driver (largest first) → residual (if any)
 * → alternative. Each row carries `start`/`end` in series units so a renderer
 * can place bars on a common axis, plus `kind`.
 */
export function waterfallRows(rec, { maxDrivers = 8 } = {}) {
  if (!rec) return [];
  const rows = [{ kind: "total", label: "Janus baseline", start: 0, end: rec.baseline, value: rec.baseline }];
  let cursor = rec.baseline;
  const top = rec.contributions.slice(0, maxDrivers);
  const rest = rec.contributions.slice(maxDrivers);
  for (const c of top) {
    rows.push({ kind: c.contribution >= 0 ? "pos" : "neg", label: c.input, start: cursor, end: cursor + c.contribution, value: c.contribution });
    cursor += c.contribution;
  }
  if (rest.length) {
    const other = rest.reduce((s, c) => s + c.contribution, 0);
    rows.push({ kind: other >= 0 ? "pos" : "neg", label: `Other (${rest.length} inputs)`, start: cursor, end: cursor + other, value: other });
    cursor += other;
  }
  if (!rec.reconciled) {
    rows.push({ kind: "resid", label: "Unexplained residual", start: cursor, end: cursor + rec.residual, value: rec.residual });
    cursor += rec.residual;
  }
  rows.push({ kind: "total", label: "Alternative scenario", start: 0, end: rec.alternative, value: rec.alternative });
  return rows;
}

/**
 * Axis for the driver bars only (not the totals): symmetric range around 0 so
 * positive and negative contributions are comparable. Returns `{min, max}`.
 */
export function driverAxis(rows) {
  let m = 0;
  for (const r of rows) if (r.kind !== "total") m = Math.max(m, Math.abs(r.value));
  return m > 0 ? { min: -m, max: m } : { min: -1, max: 1 };
}
